// Module imports
import React, { useEffect, useState } from 'react'
import { httpsCallable, getFunctions } from 'firebase/functions'
import { useFormik } from 'formik'
import { useParams, useNavigate } from 'react-router-dom'
import * as yup from 'yup'

// Component and hook imports
import Button from '../components/Button'
import InputField from '../components/InputField'
import Loader from '../components/Loader'
import useDatabaseHook from '../hooks/databaseHook'
import TokenField from '../components/TokenField'
import CopyButton from '../components/CopyButton'
import { getDatabase, ref, get } from 'firebase/database'

// Debounce timeout ref
let to

// parses dates to format used in generateToken function
const parseDate = (date, originalData) => {
  const parsedDate = date.replace(/-/g, '')
  return parsedDate
}

// Function that is called to generate the token
const generator = async (formData) => {
  // console.log('generating')
  const generateTokenFn = httpsCallable(getFunctions(), 'generateTokenV2')
  const formParse = {
    startDate: parseDate(formData.startDate),
    endDate: parseDate(formData.endDate),
    name: formData.name
  }
  // console.log('GenFormData', formData, 'GenFormParse',formParse)
  try {
    const tokenRes = await generateTokenFn(formParse)
    return tokenRes.data
  } catch (err) {
    console.error(err)
    return JSON.stringify(err)
  }
}

// Token schema used for validation in formik form
const TokenSchema = yup.object({
  name: yup.string()
    .min(3, 'Name must be larger than 3 characters')
    .max(64, 'Name must be less than 64 chacracters')
    .required('Name is required'),
  startDate: yup.date().required('Start date required').max(yup.ref('endDate'), 'Start date must be before end date'),
  endDate: yup.date().required('End date is required').min(yup.ref('startDate'), 'End date must be after start date')
})

// New Token Page
const EditToken = ({ id, token, removeToken, writeNewToken, updateToken }) => {
  // React router variables and function
  const navigate = useNavigate()
  const [genToken, setGenToken] = useState(token.token || '~~~LOADING~~~')
  const [isSaving, setIsSaving] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [savedMessage, setSavedMessage] = useState()
  const [emailTemplate, setEmailTemplate] = useState(null)

  // Fetch token email template from database
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const snap = await get(ref(getDatabase(), 'EmailTemplates/tokenAccess'))
        if (snap.val()) setEmailTemplate(snap.val())
      } catch (err) {
        console.error('Could not load email template:', err)
      }
    }
    fetchTemplate()
  }, [])

  // on click handle function for delete token
  // INFO: removeToken returns a promise but when I await it or use a .then chain it breaks the site
  // INFO: not sure if this is being called correctly but it deletes the entry in the db
  const deleteToken = (id) => {
    if (window.confirm('Do you want to delete token?')) {
      removeToken(id)
      navigate('/tokens')
    }
  }

  // formik form
  const formik = useFormik({
    initialValues: {
      startDate: token.startDate || '',
      endDate: token.endDate || '',
      name: token.name || ''
    },
    validationSchema: TokenSchema,
    isInitialValid: false,
    onSubmit: async values => {
      // Alert used for testing
      // window.alert(JSON.stringify(values, null, 2))

      // console.log('Values', values)
      // INFO: Not entirely sure if this is the best way to handle edits or new token creates but it works
      setIsSaving(true)
      if (id === 'new') {
        const result = await writeNewToken(genToken, values.name, values.startDate, values.endDate)
        setIsSaving(false)
        setSavedMessage('Token Created Successfully')

        setTimeout(() => {
          setSavedMessage()
          navigate(`/tokens/${result.key}`)
        }, 3000)
      } else {
        await updateToken(id, genToken, values.name, values.startDate, values.endDate)
        setIsSaving(false)
        setSavedMessage('Token Updated Successfully')

        setTimeout(() => {
          setSavedMessage()
        }, 3000)
      }

      // do something with the result? => redirect to tokens list
    }
  })

  // This effect helps update the the form field once the token is not just {}
  // useEffect(() => {
  //   // console.log('token is updated')
  //   if (id !== 'new') {
  //     formik.values.startDate = token.startDate
  //     formik.values.endDate = token.endDate
  //     formik.values.name = token.name
  //     setGenToken(token.token)
  //   }
  // }, [token])

  // Generate new token on data input uses debounce to passively update token value
  useEffect(() => {
    // console.log('useEffect generation')
    clearTimeout(to)
    to = setTimeout(async () => {
      setIsUpdating(true)
      let tempToken = token.token || 'Update the fields to generate a token'
      if (formik.isValid) {
        tempToken = await generator(formik.values)
      }
      setIsUpdating(false)
      // console.log(tempToken)
      setGenToken(tempToken)
      setIsUpdating(false)
    }, 100)
  }, [formik.values.name, formik.values.endDate, formik.values.startDate, formik.isValid])

  const submitButtonClass = !formik.isValid ? 'bg-slate-200 text-blue-8 hover:bg-slate-400 hover:text-blue-10 focus:outline-none' : 'focus:ring-4 focus:outline-none focus:ring-blue-3'

  return (
    <div className='mx-auto max-w-2xl p-4 text-left'>
      <div className='mb-5'>
        <button className='text-sm text-blue-6 hover:underline' onClick={() => navigate('/tokens')}>← Back to Tokens</button>
        <h1 className='mt-2 text-3xl font-bold text-gray-900'>{id === 'new' ? 'Create Token' : 'Edit Token'}</h1>
        <p className='mt-1 text-sm text-gray-500'>Generate a documentation access token and share it with the customer.</p>
      </div>

      <form onSubmit={formik.handleSubmit} className='rounded-lg border border-gray-200 bg-white p-6 shadow-sm'>
        <div className='space-y-4'>
          <div>
            <InputField
              labelFor='name'
              type='text'
              id='name'
              name='name'
              placeholder='Enter company name...'
              onChange={formik.handleChange}
              value={formik.values.name}
              onBlur={formik.handleBlur}
            >Company Name
            </InputField>
            {formik.touched.name && formik.errors.name && (
              <span className='text-sm text-red-600'>{formik.errors.name}</span>
            )}
          </div>

          <div className='flex flex-col gap-4 sm:flex-row'>
            <div className='flex-1'>
              <InputField
                labelFor='startDate'
                type='date'
                id='startDate'
                name='startDate'
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.startDate}
              >Start Date
              </InputField>
              {formik.touched.startDate && formik.errors.startDate && (
                <span className='text-sm text-red-600'>{formik.errors.startDate}</span>
              )}
            </div>
            <div className='flex-1'>
              <InputField
                labelFor='endDate'
                type='date'
                id='endDate'
                name='endDate'
                min={formik.values.startDate}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.endDate}
              >Expires At
              </InputField>
              {formik.touched.endDate && formik.errors.endDate && (
                <span className='text-sm text-red-600'>{formik.errors.endDate}</span>
              )}
            </div>
          </div>

          <div>
            {!isUpdating && (
              <TokenField
                labelFor='genToken'
                type='text'
                id='genToken'
                name='genToken'
                value={genToken}
              >Generated Token
              </TokenField>
            )}
            {isUpdating && <p className='text-sm text-gray-500'>Token updating...</p>}
          </div>
        </div>

        <div className='mt-6 flex flex-col items-center gap-3 border-t border-gray-100 pt-4 sm:flex-row sm:justify-between'>
          {savedMessage && <div className='text-sm font-medium text-green-600'>{savedMessage}</div>}
          <div className='flex flex-wrap items-center gap-2 sm:ml-auto'>
            <CopyButton
              token={genToken}
              expireDate={formik.values.endDate}
              template='email'
              customMessage={emailTemplate ? emailTemplate.body.replace(/\[token\]/g, genToken).replace(/\[expireDate\]/g, formik.values.endDate).replace(/\[name\]/g, formik.values.name) : undefined}
            >Copy Email
            </CopyButton>
            {id !== 'new' && (
              <Button type='button' disabled={isSaving} className='hover:bg-red-700 bg-red-500 focus:ring-4 focus:outline-none focus:ring-red-500' onClick={() => deleteToken(id)}>Delete</Button>
            )}
            <Button type='submit' className={submitButtonClass} disabled={!formik.isValid || isSaving || isUpdating}>Save Token</Button>
          </div>
        </div>
      </form>
    </div>
  )
}

const LoadTokenThen = () => {
  const { id } = useParams()
  const { token, writeNewToken, updateToken, removeToken } = useDatabaseHook(id) // if 'new'

  if (id !== 'new' && !token) {
    return (
      <div className='flex justify-center py-16'>
        <Loader />
      </div>
    )
  }

  return (
    <EditToken
      id={id}
      token={token || {}}
      writeNewToken={writeNewToken}
      updateToken={updateToken}
      removeToken={removeToken}
    />
  )
}

export default LoadTokenThen
